import React, { useState, useEffect, useMemo } from 'react';
import { Spinner } from 'react-bootstrap';
import { useForm } from '../../hooks/useForm';
import { useRole } from '../../hooks/useRole';
import Icon from '../common/Icons';
import ErrorNotification from '../common/ErrorNotification';
import { ROLES, getRoleLabel, ASSIGNABLE_ADMIN_ROLES } from '../../constants/roles';
import { validationRules } from '../../utils/errorHandler';
import {
  AdminModal,
  ModalFormSection,
  ModalFormGroup,
  ModalFooterActions,
  PasswordRequirements
} from './ui';
import { validatePassword } from '../../utils/passwordPolicy';

const WORK_SITE_ROLES = new Set(['head doctor', 'doctor', 'nurse', 'major', 'receptionist', 'pharmacist', 'admin pharmacist']);

const UserForm = ({
  show,
  onHide,
  onSave,
  user = null,
  isEditing = false,
  loading = false,
  workSites = []
}) => {
  const { canManageUsers } = useRole();
  const [errorNotification, setErrorNotification] = useState({
    show: false,
    message: '',
    type: 'error'
  });

  const initialValues = useMemo(
    () => ({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      username: user?.username || '',
      email: user?.email || '',
      phone: user?.phone || '',
      role: user?.role || 'user',
      work_site_ids: Array.isArray(user?.work_site_ids) ? user.work_site_ids.map((id) => String(id)) : [],
      password: '',
      confirmPassword: '',
      is_active: user?.is_active !== undefined ? user.is_active : true
    }),
    [user]
  );

  const formValidationRules = {
    last_name: validationRules.lastName,
    first_name: validationRules.firstName,
    email: validationRules.optionalEmail,
    username: (value) => {
      const trimmed = String(value || '').trim();
      if (!trimmed) return 'Le nom d\'utilisateur est requis';
      if (trimmed.length < 3) return 'Le nom d\'utilisateur doit contenir au moins 3 caractères';
      if (trimmed.length > 50) return 'Le nom d\'utilisateur ne peut pas dépasser 50 caractères';
      if (!/^[a-zA-Z0-9._-]+$/.test(trimmed)) {
        return 'Lettres, chiffres, point, tiret et underscore uniquement';
      }
      return null;
    },
    phone: validationRules.phone,
    role: (value) => {
      if (!value) return 'Le rôle est requis';
      if (!Object.values(ROLES).includes(value)) return 'Rôle invalide';
      const allowedForNewUsers = ASSIGNABLE_ADMIN_ROLES.includes(value);
      if (!isEditing && !allowedForNewUsers) return 'Ce rôle ne peut plus être attribué';
      if (isEditing && user?.role !== value && !ASSIGNABLE_ADMIN_ROLES.includes(value)) {
        return 'Ce rôle ne peut plus être attribué';
      }
      return null;
    },
    work_site_ids: (value) => {
      const selectedRole = formData.role;
      if (!WORK_SITE_ROLES.has(selectedRole)) return null;
      const ids = Array.isArray(value) ? value : [];
      if (ids.length === 0) return 'Sélectionnez au moins un site de travail pour ce rôle';
      return null;
    },
    password: (value) => {
      if (!value) {
        if (!isEditing) return 'Le mot de passe est requis';
        return null;
      }
      return validatePassword(value, { required: true });
    },
    confirmPassword: (value) => {
      if (!isEditing && !value) return 'La confirmation du mot de passe est requise';
      if (value && value !== formData.password) return 'Les mots de passe ne correspondent pas';
      return null;
    }
  };

  const {
    values: formData,
    errors,
    isSubmitting,
    handleChange,
    handleBlur,
    handleFocus,
    validateAll,
    clearErrors,
    setIsSubmitting,
    reset,
    setFieldValue
  } = useForm(initialValues, formValidationRules);

  useEffect(() => {
    if (user) {
      reset({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        username: user.username || '',
        email: user.email || '',
        phone: user.phone || '',
        role: user.role || 'user',
        work_site_ids: Array.isArray(user.work_site_ids) ? user.work_site_ids.map((id) => String(id)) : [],
        password: '',
        confirmPassword: '',
        is_active: user.is_active !== undefined ? user.is_active : true
      });
    } else {
      reset(initialValues);
    }
    setErrorNotification({ show: false, message: '', type: 'error' });
  }, [user, show, reset, initialValues]);

  useEffect(() => {
    if (WORK_SITE_ROLES.has(formData.role)) return;
    if ((formData.work_site_ids || []).length === 0) return;
    setFieldValue('work_site_ids', []);
  }, [formData.role, formData.work_site_ids, setFieldValue]);

  const roleSelectOptions = useMemo(() => {
    const values = [...ASSIGNABLE_ADMIN_ROLES];
    if (isEditing && user?.role && !values.includes(user.role)) {
      values.unshift(user.role);
    }
    return values;
  }, [isEditing, user?.role]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    clearErrors();
    setErrorNotification({ show: false, message: '', type: 'error' });

    if (!validateAll()) {
      setIsSubmitting(false);
      return;
    }

    try {
      const userData = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        username: formData.username.trim().toLowerCase(),
        email: formData.email?.trim() ? formData.email.trim().toLowerCase() : null,
        phone: formData.phone?.trim() || null,
        role: formData.role,
        is_active: formData.is_active,
        work_site_ids: WORK_SITE_ROLES.has(formData.role)
          ? (formData.work_site_ids || []).map((id) => Number(id))
          : []
      };

      if (formData.password) {
        userData.password = formData.password;
      }

      const result = await onSave(userData, isEditing ? user.id : null);

      if (result.success) {
        onHide();
        reset(initialValues);
      } else {
        setErrorNotification({
          show: true,
          message: result.message || 'Erreur lors de la sauvegarde',
          type: 'error'
        });
      }
    } catch (error) {
      setErrorNotification({
        show: true,
        message: error.message || 'Erreur lors de la sauvegarde',
        type: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onHide();
      reset(initialValues);
      setErrorNotification({ show: false, message: '', type: 'error' });
    }
  };

  const toggleWorkSite = (siteId) => {
    const id = String(siteId);
    const current = formData.work_site_ids || [];
    const next = current.includes(id) ? current.filter((v) => v !== id) : [...current, id];
    setFieldValue('work_site_ids', next);
  };

  const showWorkSiteSelector = WORK_SITE_ROLES.has(formData.role);
  const busy = isSubmitting || loading;
  const title = isEditing ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur';
  const submitLabel = busy ? (isEditing ? 'Modification…' : 'Création…') : isEditing ? 'Enregistrer' : 'Créer le compte';

  return (
    <AdminModal
      show={show}
      onHide={handleClose}
      size="lg"
      icon={isEditing ? 'edit' : 'userPlus'}
      badge={isEditing ? 'Édition' : 'Création'}
      title={title}
      subtitle={
        isEditing
          ? 'Mettez à jour les informations et les droits d\'accès.'
          : 'Renseignez l\'identité, le rôle et les sites de travail si nécessaire.'
      }
      closeOnBackdrop={!busy}
      footer={
        <ModalFooterActions
          onCancel={handleClose}
          primary={
            <button
              type="submit"
              form="admin-user-form"
              className="adm-modal-btn adm-modal-btn--primary"
              disabled={busy}
            >
              {busy && <Spinner animation="border" size="sm" />}
              <Icon name={isEditing ? 'check' : 'plus'} size={14} />
              {submitLabel}
            </button>
          }
        />
      }
    >
      <form id="admin-user-form" className="adm-modal-form" onSubmit={handleSubmit} noValidate>
        <ErrorNotification
          show={errorNotification.show}
          message={errorNotification.message}
          type={errorNotification.type}
          onClose={() => setErrorNotification({ show: false, message: '', type: 'error' })}
        />

        <ModalFormSection title="Identité" hint="Nom affiché dans l'application et identifiant de connexion.">
          <div className="adm-modal-form__grid">
            <ModalFormGroup label="Nom" required htmlFor="user-last-name" error={errors.last_name}>
              <input
                id="user-last-name"
                type="text"
                name="last_name"
                className={`adm-modal-input ${errors.last_name ? 'is-invalid' : ''}`}
                value={formData.last_name}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={() => handleFocus('last_name')}
                placeholder="Ex. Rakoto"
                disabled={busy}
              />
            </ModalFormGroup>

            <ModalFormGroup label="Prénom" htmlFor="user-first-name" error={errors.first_name}>
              <input
                id="user-first-name"
                type="text"
                name="first_name"
                className={`adm-modal-input ${errors.first_name ? 'is-invalid' : ''}`}
                value={formData.first_name}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={() => handleFocus('first_name')}
                placeholder="Ex. Jean"
                disabled={busy}
              />
            </ModalFormGroup>

            <ModalFormGroup label="Nom d'utilisateur" required htmlFor="user-username" error={errors.username}>
              <input
                id="user-username"
                type="text"
                name="username"
                className={`adm-modal-input ${errors.username ? 'is-invalid' : ''}`}
                value={formData.username}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={() => handleFocus('username')}
                placeholder="Ex. j.rakoto"
                disabled={busy}
                autoComplete="username"
              />
            </ModalFormGroup>

            <ModalFormGroup label="Email" htmlFor="user-email" error={errors.email}>
              <input
                id="user-email"
                type="email"
                name="email"
                className={`adm-modal-input ${errors.email ? 'is-invalid' : ''}`}
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={() => handleFocus('email')}
                placeholder="Ex. jean.rakoto@funtoa.com"
                disabled={busy}
                autoComplete="email"
              />
            </ModalFormGroup>

            <ModalFormGroup label="Téléphone" htmlFor="user-phone" error={errors.phone}>
              <input
                id="user-phone"
                type="tel"
                name="phone"
                className={`adm-modal-input ${errors.phone ? 'is-invalid' : ''}`}
                value={formData.phone}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={() => handleFocus('phone')}
                placeholder="Ex. +261 34 12 345 67"
                disabled={busy}
                autoComplete="tel"
              />
            </ModalFormGroup>
          </div>
        </ModalFormSection>

        <ModalFormSection title="Accès" hint="Rôle et statut du compte sur la plateforme.">
          <div className="adm-modal-form__grid">
            <ModalFormGroup label="Rôle" required htmlFor="user-role" error={errors.role}>
              <select
                id="user-role"
                name="role"
                className={`adm-modal-select ${errors.role ? 'is-invalid' : ''}`}
                value={formData.role}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={() => handleFocus('role')}
                disabled={busy || !canManageUsers()}
              >
                {roleSelectOptions.map((value) => (
                  <option key={value} value={value}>
                    {getRoleLabel(value)}
                  </option>
                ))}
              </select>
            </ModalFormGroup>

            <ModalFormGroup label="Statut" htmlFor="user-status">
              <select
                id="user-status"
                name="is_active"
                className="adm-modal-select"
                value={formData.is_active ? '1' : '0'}
                onChange={(e) => setFieldValue('is_active', e.target.value === '1')}
                disabled={busy}
              >
                <option value="1">Actif</option>
                <option value="0">Inactif</option>
              </select>
            </ModalFormGroup>

            {showWorkSiteSelector && (
              <ModalFormGroup
                label="Sites de travail"
                required
                fullWidth
                error={errors.work_site_ids}
              >
                {workSites.length === 0 ? (
                  <p className="adm-modal-field__hint">Aucun site actif disponible.</p>
                ) : (
                  <div className="adm-modal-chips" role="group" aria-label="Sites de travail">
                    {workSites.map((site) => {
                      const selected = (formData.work_site_ids || []).includes(String(site.id));
                      return (
                        <button
                          key={site.id}
                          type="button"
                          className={`adm-modal-chip ${selected ? 'is-selected' : ''}`}
                          onClick={() => toggleWorkSite(site.id)}
                          disabled={busy}
                          aria-pressed={selected}
                        >
                          <span className="adm-modal-chip__check" aria-hidden>
                            {selected ? <Icon name="check" size={10} /> : null}
                          </span>
                          {site.name}
                        </button>
                      );
                    })}
                  </div>
                )}
                <p className="adm-modal-field__hint">Cliquez pour sélectionner un ou plusieurs sites.</p>
              </ModalFormGroup>
            )}
          </div>
        </ModalFormSection>

        <ModalFormSection
          title="Mot de passe"
          hint={isEditing ? 'Laissez vide pour conserver le mot de passe actuel.' : undefined}
        >
          <div className="adm-modal-form__grid">
            <ModalFormGroup
              label={isEditing ? 'Nouveau mot de passe' : 'Mot de passe'}
              required={!isEditing}
              htmlFor="user-password"
              error={errors.password}
            >
              <input
                id="user-password"
                type="password"
                name="password"
                className={`adm-modal-input ${errors.password ? 'is-invalid' : ''}`}
                value={formData.password}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={() => handleFocus('password')}
                placeholder={isEditing ? 'Laisser vide pour conserver le mot de passe actuel' : 'Ex. Funtoa2026'}
                disabled={busy}
                autoComplete={isEditing ? 'new-password' : 'new-password'}
                aria-describedby="user-password-requirements"
              />
            </ModalFormGroup>

            <ModalFormGroup
              label="Confirmation"
              required={!isEditing}
              htmlFor="user-confirm-password"
              error={errors.confirmPassword}
            >
              <input
                id="user-confirm-password"
                type="password"
                name="confirmPassword"
                className={`adm-modal-input ${errors.confirmPassword ? 'is-invalid' : ''}`}
                value={formData.confirmPassword}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={() => handleFocus('confirmPassword')}
                placeholder="Ressaisir le mot de passe"
                disabled={busy}
                autoComplete="new-password"
                aria-describedby="user-password-requirements"
              />
            </ModalFormGroup>

            <div className="adm-modal-field--full" id="user-password-requirements">
              <PasswordRequirements
                password={formData.password}
                confirmPassword={formData.confirmPassword}
                showConfirmMatch={!isEditing || Boolean(formData.password || formData.confirmPassword)}
                active={!isEditing || Boolean(formData.password || formData.confirmPassword)}
              />
              {isEditing && !formData.password && !formData.confirmPassword && (
                <p className="adm-modal-field__hint mb-0 mt-2">
                  Les critères s&apos;affichent dès que vous saisissez un nouveau mot de passe.
                </p>
              )}
            </div>
          </div>
        </ModalFormSection>
      </form>
    </AdminModal>
  );
};

export default UserForm;
